import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { AdminDashboardData } from '../../types/api';

export const AdminDashboardPage: React.FC = () => {
    const [data, setData] = useState<AdminDashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        api.adminGetDashboard().then(res => {
            if (res.data) setData(res.data);
        }).catch(() => {}).finally(() => {
            setLoading(false);
        });
    }, []);

    const stats = data?.stats || {
        published_posts: 0,
        draft_posts: 0,
        total_pages: 0,
        experiences: 0,
        unread_contacts: 0,
        total_media: 0,
    };
    const recentPosts = data?.recentPosts || [];
    const recentContacts = data?.recentContacts || [];
    const recentAuditLogs = data?.recentAuditLogs || [];

    return (
        <div className="space-y-8">
            {/* Top Header & Quick Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Overview Dashboard</h1>
                    <p className="text-sm text-slate-400 mt-1">Real-time CMS statistics, active records, and recent updates.</p>
                </div>

                <div className="flex items-center gap-3">
                    <Link to="/admin/posts/new" className="px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg transition">
                        + New Article
                    </Link>
                    <Link to="/admin/media" className="px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-slate-900 border border-slate-700 hover:border-slate-500 text-slate-200 transition">
                        Upload Media
                    </Link>
                </div>
            </div>

            {/* Statistics Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                
                <div className="p-6 rounded-2xl glass-card space-y-2">
                    <div className="flex items-center justify-between text-xs text-slate-400 font-semibold uppercase tracking-wider">
                        <span>Published Posts</span>
                        <span className="text-emerald-400">Articles</span>
                    </div>
                    <div className="text-3xl font-extrabold text-white">{stats.published_posts}</div>
                    <div className="text-xs text-slate-400">{stats.draft_posts} drafts pending</div>
                </div>

                <div className="p-6 rounded-2xl glass-card space-y-2">
                    <div className="flex items-center justify-between text-xs text-slate-400 font-semibold uppercase tracking-wider">
                        <span>Leadership Roles</span>
                        <span className="text-emerald-400">Verified</span>
                    </div>
                    <div className="text-3xl font-extrabold text-white">{stats.experiences}</div>
                    <div className="text-xs text-slate-400">Executive & community records</div>
                </div>

                <div className="p-6 rounded-2xl glass-card space-y-2">
                    <div className="flex items-center justify-between text-xs text-slate-400 font-semibold uppercase tracking-wider">
                        <span>Unread Inquiries</span>
                        {stats.unread_contacts > 0 ? (
                            <span className="text-rose-400 animate-pulse">Action needed</span>
                        ) : (
                            <span className="text-slate-400">Clear</span>
                        )}
                    </div>
                    <div className="text-3xl font-extrabold text-white">{stats.unread_contacts}</div>
                    <Link to="/admin/contact" className="text-xs text-emerald-400 hover:underline block">
                        Open Contact Inbox &rarr;
                    </Link>
                </div>

                <div className="p-6 rounded-2xl glass-card space-y-2">
                    <div className="flex items-center justify-between text-xs text-slate-400 font-semibold uppercase tracking-wider">
                        <span>Media Library</span>
                        <span className="text-emerald-400">WebP</span>
                    </div>
                    <div className="text-3xl font-extrabold text-white">{stats.total_media}</div>
                    <div className="text-xs text-slate-400">Optimized image assets</div>
                </div>

            </div>

            {/* Two-Column Grid: Recent Posts & Recent Inquiries */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Recent Blog Posts */}
                <div className="p-6 rounded-3xl glass-card space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-white">Recent Articles</h2>
                        <Link to="/admin/posts" className="text-xs text-emerald-400 hover:underline">View all</Link>
                    </div>

                    <div className="divide-y divide-slate-800">
                        {recentPosts.length > 0 ? (
                            recentPosts.map(post => (
                                <div key={post.id} className="py-3.5 flex items-center justify-between gap-4">
                                    <div className="truncate">
                                        <Link to={`/admin/posts/${post.id}`} className="text-sm font-semibold text-white hover:text-emerald-400 transition truncate block">
                                            {post.title}
                                        </Link>
                                        <span className="text-xs text-slate-400">
                                            {post.category?.name || 'General'} • {post.reading_time_minutes} min read
                                        </span>
                                    </div>
                                    <div>
                                        {post.status === 'published' ? (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                Published
                                            </span>
                                        ) : (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                                Draft
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="py-4 text-xs text-slate-500 text-center">No posts found.</p>
                        )}
                    </div>
                </div>

                {/* Recent Contact Submissions */}
                <div className="p-6 rounded-3xl glass-card space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-white">Recent Inquiries</h2>
                        <Link to="/admin/contact" className="text-xs text-emerald-400 hover:underline">View inbox</Link>
                    </div>

                    <div className="divide-y divide-slate-800">
                        {recentContacts.length > 0 ? (
                            recentContacts.map(contact => (
                                <div key={contact.id} className="py-3.5 flex items-center justify-between gap-4">
                                    <div className="truncate">
                                        <div className="text-sm font-semibold text-white truncate">
                                            {contact.subject}
                                        </div>
                                        <span className="text-xs text-slate-400">
                                            From {contact.name} ({contact.email})
                                        </span>
                                    </div>
                                    <div>
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${contact.status === 'unread' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                                            {contact.status}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="py-4 text-xs text-slate-500 text-center">No contact inquiries.</p>
                        )}
                    </div>
                </div>

            </div>

            {/* Recent Audit Logs */}
            <div className="p-6 rounded-3xl glass-card space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-white">Security & Audit Trails</h2>
                    <Link to="/admin/audit" className="text-xs text-emerald-400 hover:underline">View all logs</Link>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-300">
                        <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-800">
                            <tr>
                                <th className="pb-3 font-semibold">Action</th>
                                <th className="pb-3 font-semibold">User</th>
                                <th className="pb-3 font-semibold">Details</th>
                                <th className="pb-3 font-semibold text-right">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                            {recentAuditLogs.length > 0 ? (
                                recentAuditLogs.map(log => (
                                    <tr key={log.id} className="hover:bg-slate-800/30">
                                        <td className="py-3 font-medium text-emerald-400">{log.action}</td>
                                        <td className="py-3 text-slate-300">{log.user?.name || 'System'}</td>
                                        <td className="py-3 text-slate-400 text-xs truncate max-w-xs">{log.details}</td>
                                        <td className="py-3 text-slate-500 text-xs text-right font-mono">
                                            {new Date(log.created_at).toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="py-4 text-center text-xs text-slate-500">No logs recorded yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};
